import './TourAveragesTable.scss';

import { Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs } from '@trackman/web-shared-components';
import { Trans, useTranslation } from 'react-i18next';
import { clubs, clubsNamesMapper } from 'utilities/clubNamesMapper';
import { cultureToUnitsMapper, unitNamesMapper } from 'utilities/unitNamesMapper';
import { useEffect, useMemo, useState } from 'react';

import Paragraph from 'components/TextComps/Paragraph';
import { ParameterModel } from 'types/trackmanParameters';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';

type Props = {
  visibleParameter: ParameterModel;
};

const getNonEmptyUnits = (data: Record<string, any> | undefined) => Object.keys(data || {}).filter((unit) => data![unit]);

const valueDisplay = (value, activeUnitTabName: string) => {
  let unitValue = '-';

  if (activeUnitTabName === 'deg' && value !== null) {
    unitValue = `${value}°`;
  } else if (['yards', 'meters'].includes(activeUnitTabName) && typeof value === 'number') {
    unitValue = value.toFixed(0);
  } else if (typeof value === 'number') {
    unitValue = Number.isInteger(value) ? parseFloat(value.toFixed(2)).toString() : value.toFixed(2);
  }
  return unitValue;
};

export const TourAveragesTable = observer(({ visibleParameter }: Props) => {
  const { t } = useTranslation();
  const ps = useStores().profileStore;
  const units = useMemo(() => cultureToUnitsMapper(ps.profileData.culture), [ps.profileData.culture]);

  const [activeUnitTabIndex, setActiveUnitTabIndex] = useState<number | null>(null);
  const [activeUnitTabName, setActiveUnitTabName] = useState<string | null>(null);
  const [unitsToDisplay, setUnitsToDisplay] = useState<string[]>([]);

  useEffect(() => {
    const pgaUnits = getNonEmptyUnits(visibleParameter.averageTourData?.pga);
    const lpgaUnits = getNonEmptyUnits(visibleParameter.averageTourData?.lpga);
    const filteredUnits = pgaUnits.length ? pgaUnits : lpgaUnits ? lpgaUnits : [];
    const activeUnitIndex = filteredUnits.findIndex((unit) => units.includes(unit));

    setUnitsToDisplay(filteredUnits);
    setActiveUnitTabIndex(activeUnitIndex);
    setActiveUnitTabName(filteredUnits[activeUnitIndex] || filteredUnits[0]);
  }, [units, visibleParameter.averageTourData]);

  return (
    <div className='parameter-tour-averages'>
      <div className='parameter-tour-averages-heading'>
        <h3>{t('TrackmanTourData', { ns: 'trackmanParameters' })}</h3>
        <Paragraph>
          {t('DiscoverTheLatestAverageOfParameterPerClub', { ns: 'trackmanParameters', parameterTitle: visibleParameter.title })}
        </Paragraph>
      </div>
      <div className='tabs-wrapper'>
        {unitsToDisplay.length ? (
          <Tabs value={activeUnitTabIndex}>
            {unitsToDisplay.map((unit, index) => {
              if (unit === 'withoutUnit') return null;
              return (
                <Tab
                  label={t(unitNamesMapper(unit), { ns: 'trackmanParameters' })}
                  key={unit}
                  onClick={() => {
                    setActiveUnitTabIndex(index);
                    setActiveUnitTabName(unit);
                  }}
                />
              );
            })}
          </Tabs>
        ) : null}
        <Paragraph>
          <>{t('DataFromYearOfCollection', { ns: 'trackmanParameters', yearOfDataCollection: visibleParameter.averageTourData?.year })}</>
        </Paragraph>
      </div>
      <Table className='tmu-table'>
        <TableHead>
          <TableRow>
            <TableCell width='80px'> </TableCell>
            {clubs.map((club) => {
              return (
                <TableCell key={club} width='80px'>
                  {t(clubsNamesMapper(club), { ns: 'trackmanParameters' })}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleParameter.averageTourData?.pga ? (
            <TableRow>
              <TableCell width='80px'>{t('PGA', { ns: 'trackmanParameters' })}</TableCell>
              {visibleParameter.averageTourData?.pga &&
                activeUnitTabName &&
                visibleParameter.averageTourData.pga[activeUnitTabName] &&
                Object.entries(visibleParameter.averageTourData.pga[activeUnitTabName]).map(([unit, value], index) => {
                  return <TableCell key={`pga-${unit}-${index}`}>{valueDisplay(value, activeUnitTabName)}</TableCell>;
                })}
            </TableRow>
          ) : null}
          {visibleParameter.averageTourData?.lpga ? (
            <TableRow>
              <TableCell width='80px'>{t('LPGA', { ns: 'trackmanParameters' })}</TableCell>
              {visibleParameter.averageTourData?.lpga &&
                activeUnitTabName &&
                visibleParameter.averageTourData.lpga[activeUnitTabName] &&
                Object.entries(visibleParameter.averageTourData.lpga[activeUnitTabName]).map(([unit, value], index) => {
                  return <TableCell key={`lpga-${unit}-${index}`}>{valueDisplay(value, activeUnitTabName)}</TableCell>;
                })}
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <Paragraph>
        <>
          <Trans i18nKey='GetTheFullListOfTourAverages' ns='trackmanParameters'>
            Get the full list of our tour averages for all available parameters&nbsp;
            <a
              href='https://www.trackman.com/blog/golf/introducing-updated-tour-averages'
              title={t('SeeTheFullList', { ns: 'trackmanParameters' })}
              target='_blank'
              rel='noreferrer'
            >
              here
            </a>
            .
          </Trans>
        </>
      </Paragraph>
    </div>
  );
});

export default TourAveragesTable;
